import { Link } from "react-router-dom"
import { useHead, useSeoMeta } from "@unhead/react"
import {Check, Sparkles, Heart, ArrowRight, MapPin} from 'lucide-react'
import { formulesMaman, images, prestations, serviceArea, travelFees } from "@/data/site"
import { jsonLdGraphScript, pageCanonical } from "@/lib/seo"

function ServicesMaman() {
  useSeoMeta({
    title: "Massage Prénatal & Postnatal Lorient, Plouay",
    description:
      "Prestations maman à Plouay et Lorient : massage prénatal, postnatal, rituels bien-être. Doula Maïwenn Keraudran, zone Morbihan (35 km).",
    ogTitle: "Massage Prénatal & Postnatal | Au Creux de Maman",
    ogDescription:
      "Massages pré/postnatal et soins pour la maman à Plouay, Lorient et alentours.",
    ogImage: images.massagePrenatal,
    twitterCard: "summary_large_image",
  })

  const mamanPageUrl = pageCanonical("/prestations-maman")
  const mamanMassages = prestations.filter((p) => p.id !== "doula")

  useHead({
    script: [
      jsonLdGraphScript(
        [
          ...mamanMassages.map((prestation) => ({
            name: prestation.title,
            description: prestation.summary,
            url: `${mamanPageUrl}#${prestation.id}`,
            image: prestation.image,
            serviceType: "Massage bien-être périnatal",
            offers: prestation.pricing,
          })),
          ...formulesMaman.map((formule) => ({
            name: formule.title,
            description: formule.description,
            url: `${mamanPageUrl}#${formule.id}`,
            image: formule.image,
            serviceType: "Formule massages maman",
            offers: [{ label: formule.subtitle, price: formule.price }],
          })),
        ],
        "jsonld-offers-maman",
      ),
    ],
  })

  return (
    <div className="text-[#2d221b]">
      <section className="mx-auto max-w-6xl px-5 pb-8 pt-14 md:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#d6c4b6] bg-[#efe1d4]/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">
          <Heart size={14} />
          <span>Prestations Maman</span>
        </div>
        <h1 className="mt-4 max-w-3xl font-display text-4xl font-medium leading-tight text-[#2d221b] md:text-6xl">
          Accompagnement & soins pour la maman
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5e4b3e] md:text-lg">
          De la grossesse au post-partum, un espace dédié pour prendre soin de vous, déposer vos émotions
          et vivre cette aventure en toute sérénité.
        </p>
      </section>

      <section className="mx-auto max-w-6xl space-y-10 px-5 pb-16 md:px-8">
        {prestations
          .filter((p) => p.id !== "doula")
          .map((prestation, index) => (
          <article
            key={prestation.id}
            id={prestation.id}
            className={`grid overflow-hidden rounded-3xl border border-[#dfd0c2] bg-[#fbf7f2] shadow-warm-xs md:grid-cols-2 ${
              index % 2 === 1 ? "md:[&>img]:order-2" : ""
            }`}
          >
            <div className="p-3 md:p-4">
              <div className="aspect-square w-full overflow-hidden rounded-2xl bg-stone-100">
                <img
                  src={prestation.image}
                  alt={prestation.title}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              </div>
              <p className="mt-1.5 px-1 text-center text-[10px] italic text-[#8c6d58]">Photo non contractuelle</p>
            </div>
            <div className="flex flex-col justify-between p-8 md:p-12">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">{prestation.kicker}</p>
                <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">{prestation.title}</h2>
                <p className="mt-3 whitespace-pre-line text-xs leading-relaxed text-[#685548] md:text-sm">{prestation.summary}</p>
                <ul className="mt-6 space-y-2.5">
                  {prestation.details.map((detail) => (
                    <li key={detail} className="flex items-start gap-2.5 text-xs leading-relaxed text-[#5e4b3e]">
                      <Check size={15} className="mt-0.5 shrink-0 text-[#b85c38]" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                {"note" in prestation && prestation.note ? (
                  <p className="mt-4 text-xs italic leading-relaxed text-[#8c6d58]">{prestation.note}</p>
                ) : null}
              </div>

              <div className="mt-7">
                <div className="divide-y divide-[#ede0d2] rounded-2xl border border-[#ebdcd0] bg-white p-4.5 shadow-warm-xs">
                  {prestation.pricing.map((tarif) => (
                    <div key={tarif.label} className="flex items-center justify-between gap-4 py-2.5 first:pt-0 last:pb-0 text-sm">
                      <span className="text-xs font-medium text-[#4a3a30] leading-snug">{tarif.label}</span>
                      <span className="shrink-0 text-right font-display text-base font-bold text-[#8a4225] tabular-nums whitespace-nowrap">
                        {tarif.price}
                      </span>
                    </div>
                  ))}
                </div>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#b85c38] to-[#9d4c2e] px-6 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white shadow-warm-sm transition-all hover:scale-[1.02] hover:shadow-warm-md"
                >
                  <span>Prendre contact</span>
                  <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* Formules Duo & Complète */}
      <section className="mx-auto max-w-6xl px-5 pb-16 md:px-8">
        <div className="rounded-3xl border border-[#dfd0c2] bg-gradient-to-br from-[#fbf7f2] via-[#f7efe6] to-[#efe1d4] p-8 md:p-12 shadow-warm-xs">
          <div className="flex items-center gap-2">
            <Sparkles size={18} className="text-[#b85c38]" />
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#b85c38]">Formules Avantageuses</p>
          </div>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">
            Combinez vos soins pour un suivi tout en douceur
          </h2>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            Bénéficiez d'un tarif préférentiel en associant plusieurs prestations pour vous accompagner à chaque étape (avant, pendant, après).
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-2">
            {formulesMaman.map((formule) => (
              <div
                key={formule.id}
                id={formule.id}
                className="flex flex-col justify-between rounded-2xl border border-[#ebdcd0] bg-white p-7 shadow-warm-xs transition-all hover:shadow-warm-sm"
              >
                <div>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-display text-2xl font-medium text-[#2d221b]">{formule.title}</h3>
                      <p className="text-xs font-semibold text-[#b85c38] mt-0.5">{formule.subtitle}</p>
                    </div>
                    <span className="shrink-0 rounded-xl bg-[#efe1d4] px-3.5 py-1.5 font-display text-2xl font-bold text-[#8a4225] tabular-nums">
                      {formule.price}
                    </span>
                  </div>
                  <p className="mt-4 text-xs leading-relaxed text-[#685548]">{formule.description}</p>
                </div>
                <div className="mt-6 flex items-center justify-between border-t border-[#ede0d2] pt-5">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#8c6d58]">
                    {formule.savings}
                  </span>
                  <Link
                    to="/contact"
                    className="rounded-full bg-[#6f4b3e] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.14em] text-white transition hover:bg-[#5a3c32]"
                  >
                    Réserver
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="bg-[#efe1d4] border-y border-[#dfd0c2]">
        <div className="mx-auto max-w-6xl px-5 py-16 md:px-8">
          <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">
            <MapPin size={14} />
            <span>Déplacements</span>
          </div>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Zone d'intervention</h2>
          <p className="mt-3 max-w-2xl text-xs leading-relaxed text-[#5e4b3e] md:text-sm">
            {serviceArea}
          </p>
          <p className="mt-2 max-w-2xl text-xs text-[#8c6d58] leading-relaxed">
            {travelFees}
          </p>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-8 px-5 py-16 md:grid-cols-2 md:px-8">
        <div className="overflow-hidden rounded-3xl border border-[#dfd0c2] bg-white p-2.5 shadow-warm-xs">
          <img
            src={images.kit}
            alt="Objets doux d'accompagnement périnatal"
            loading="lazy"
            className="h-80 w-full rounded-2xl object-cover"
          />
        </div>
        <div className="flex flex-col justify-center">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#b85c38]">Accompagnement personnalisé</span>
          <h2 className="mt-2 font-display text-3xl font-medium text-[#2d221b] md:text-4xl">Une question, un besoin particulier ?</h2>
          <p className="mt-4 text-xs leading-relaxed text-[#685548] md:text-sm">
            Chaque grossesse et chaque naissance sont différentes. Si votre besoin ne correspond pas
            exactement à l'une de ces prestations, parlons-en : nous adapterons l'accompagnement ensemble.
          </p>
          <p className="mt-3 text-xs text-[#8c6d58] italic">
            L'accompagnement d'une doula ne remplace en aucun cas le suivi médical assuré par une
            sage-femme ou un gynécologue-obstétricien.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex w-fit items-center gap-2 rounded-full border border-[#cbbab0] bg-white/90 px-6 py-3 text-xs font-medium uppercase tracking-[0.14em] text-[#5e4b3e] shadow-sm transition hover:border-[#b85c38] hover:text-[#2d221b]"
          >
            <span>Me contacter</span>
            <ArrowRight size={13} />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default ServicesMaman
